import type { AnyFieldMetaBase, Updater } from '@tanstack/react-form';

export interface ZodLikeIssue {
  path?: ReadonlyArray<PropertyKey>;
  message: string;
}

export type ZodIssues = ReadonlyArray<ZodLikeIssue>;

export interface TanStackFormLike {
  setFieldMeta: (fieldName: never, updater: Updater<AnyFieldMetaBase>) => void;
}

export function pathToFieldName(path: ReadonlyArray<PropertyKey>): string {
  if (!path.length) return '';
  let out = String(path[0]);
  for (const segment of path.slice(1)) {
    if (typeof segment === 'number') {
      out += `[${segment}]`;
      continue;
    }
    out += `.${String(segment)}`;
  }
  return out;
}

/**
 * Push Zod-ish issues into TanStack Form's field meta.
 *
 * TanStack Form's built-in onSubmit validator only runs during actual form submission
 * (form.handleSubmit()), not when calling validateAllFields('submit'). When we run
 * additional Zod validation gates, we manually push field errors so adapters can display them.
 */
export function pushZodIssuesToTanStackForm(form: TanStackFormLike, issues: ZodIssues): void {
  const errorsByField = new Map<string, string[]>();

  for (const issue of issues) {
    const path = issue.path ?? [];
    const name = pathToFieldName(path);
    if (!name) continue;

    const existing = errorsByField.get(name) ?? [];
    existing.push(issue.message);
    errorsByField.set(name, existing);
  }

  for (const [fieldName, messages] of errorsByField) {
    form.setFieldMeta(fieldName as never, (prev) => {
      const errorMap =
        (prev as unknown as { errorMap?: Record<string, unknown> } | undefined)?.errorMap ?? {};
      return {
        ...prev,
        isTouched: true,
        isValid: false,
        errors: messages.map((msg) => ({ message: msg })),
        errorMap: {
          ...errorMap,
          onSubmit: messages.join('; '),
        },
      } as AnyFieldMetaBase;
    });
  }
}
